/**
 * 예산배정 PoC
 * - 계획수립: 월별예산과 동일 레이아웃 · 월별 계획은 편성 월별예산과 동일 개념
 * - 처리: 정기배정(계획 기반) / 수시배정(계획 외)
 * - 승인: 처리(요청) 건 승인
 *
 * 키:
 *   erp.budget.allocPlan|{tenant}|{fy}|{kind}|{inout}
 *   erp.budget.allocProcess|{tenant}|{fy}|{kind}|{inout}
 */

import {
  MONTH_FIELDS,
  emptyMonthAmounts,
  enrichMonthlyRow,
  monthlyLineKey,
  withMonthlyGridIds,
  buildMonthlyRowsFromMasters,
  loadMonthlyBudgets,
  distributeMonthly,
} from 'budget/budgetMonthlyBudgetStore';
import { getCachedBalances, filterBalances } from 'budget/budgetBalanceStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';

export const ALLOC_PLAN_LABEL = '예산배정계획';
export const ALLOC_PROCESS_LABEL = '예산배정처리';
export const ALLOC_APPROVE_LABEL = '예산배정승인';

export const ALLOC_TYPE = {
  REGULAR: 'REGULAR',
  ADHOC: 'ADHOC',
};

export const ALLOC_TYPE_OPTIONS = [
  { value: ALLOC_TYPE.REGULAR, label: '정기배정 (계획)' },
  { value: ALLOC_TYPE.ADHOC, label: '수시배정 (계획외)' },
];

const PLAN_PREFIX = 'erp.budget.allocPlan';
const PROCESS_PREFIX = 'erp.budget.allocProcess';

const PLAN_SAVE_QUERY = [
  'BdBudgetExec.insertAllocPlan',
  'BdBudgetExec.updateAllocPlan',
  'BdBudgetExec.deleteAllocPlan',
];
const PROCESS_SAVE_QUERY = [
  'BdBudgetExec.insertAllocProcess',
  'BdBudgetExec.updateAllocProcess',
  'BdBudgetExec.deleteAllocProcess',
];

const planCache = new Map();
const processCache = new Map();
const planMetaCache = new Map();

function allocQueryDims(dims = {}) {
  return {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    budgetKindCode: dims.budgetKindCode,
    inoutDiv: dims.inoutDiv || '지출',
  };
}

function mapAllocRowFromApi(row) {
  return enrichAllocRow({
    ...row,
    fundUid: row.fundUid || row.fundCode || '',
  });
}

function derivePlanMeta(rows = []) {
  const hit = (rows || []).find((r) => String(r.planConfirmYn || '').toUpperCase() === 'Y');
  const src = hit || rows[0];
  if (!src) {
    return { planConfirmYn: 'N', planConfirmAt: null, planConfirmBy: '' };
  }
  return {
    planConfirmYn: String(src.planConfirmYn || '').toUpperCase() === 'Y' ? 'Y' : 'N',
    planConfirmAt: src.planConfirmAt || null,
    planConfirmBy: src.planConfirmBy || '',
  };
}

function dimsKey(prefix, dims = {}) {
  return [
    prefix,
    String(dims.tenantCode || '').trim(),
    String(dims.fiscalYear || '').trim(),
    String(dims.budgetKindCode || '').trim(),
    String(dims.inoutDiv || '지출').trim(),
  ].join('|');
}

function readRows(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

function writeRows(key, rows, extra = {}) {
  localStorage.setItem(
    key,
    JSON.stringify({
      rows: rows || [],
      savedAt: new Date().toISOString(),
      ...extra,
    })
  );
  return rows || [];
}

function readMeta(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

function toInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

/** 현액(편성확정) → 배정 마스터 행 */
export function mastersFromConfirmedBalances(dims = {}) {
  const { tenantCode, fiscalYear, budgetKindCode, inoutDiv } = dims;
  if (!tenantCode || !fiscalYear) return [];
  const all = getCachedBalances({ tenantCode, fiscalYear });
  const filtered = filterBalances(all, {
    budgetKindCode: budgetKindCode || '',
    inoutDiv: inoutDiv || '',
  });
  return filtered.map((r) => ({
    requestUid: String(r.sourceRequestUid || r.balanceUid || '').trim(),
    balanceUid: r.balanceUid || '',
    deptCode: r.deptCode || '',
    deptName: r.deptName || '',
    projectCode: r.projectCode || '',
    projectName: r.projectName || '',
    subjectCode: r.subjectCode || r.subjectUid || '',
    subjectDispCode: r.subjectDispCode || r.subjectCode || '',
    subjectName: r.subjectName || '',
    fundUid: r.fundUid || '',
    fundCode: r.fundCode || '',
    fundName: r.fundName || '',
    acctClassCode: r.acctClassCode || '',
    budgetAmt: toInt(r.confirmedAmt),
    inoutDiv: r.inoutDiv || inoutDiv || '지출',
    budgetKindCode: r.budgetKindCode || budgetKindCode || '',
  }));
}

export function enrichAllocRow(row = {}) {
  const base = enrichMonthlyRow(row);
  return {
    ...base,
    allocType: row.allocType === ALLOC_TYPE.ADHOC ? ALLOC_TYPE.ADHOC : ALLOC_TYPE.REGULAR,
    planConfirmYn: String(row.planConfirmYn || '').toUpperCase() === 'Y' ? 'Y' : 'N',
    processYn: String(row.processYn || '').toUpperCase() === 'Y' ? 'Y' : 'N',
    processAt: row.processAt || null,
    processBy: row.processBy || '',
    approveYn: String(row.approveYn || '').toUpperCase() === 'Y' ? 'Y' : 'N',
    approveAt: row.approveAt || null,
    approveBy: row.approveBy || '',
    allocUid: row.allocUid || '',
    /** 예산변경에서 배정까지 승인 시 추적키 */
    changeNo: row.changeNo || '',
    changeUid: row.changeUid || '',
    changeDetailUid: row.changeDetailUid || '',
    changeAmt: row.changeAmt != null ? Number(row.changeAmt) || 0 : 0,
  };
}

function persistShape(row, metaPatch = null) {
  const e = enrichAllocRow(row);
  const out = {
    allocUid: e.allocUid || '',
    lineKey: e.lineKey || monthlyLineKey(e),
    requestUid: e.requestUid || '',
    balanceUid: e.balanceUid || '',
    deptCode: e.deptCode || '',
    deptName: e.deptName || '',
    projectCode: e.projectCode || '',
    projectName: e.projectName || '',
    subjectCode: e.subjectCode || '',
    subjectName: e.subjectName || '',
    fundUid: e.fundUid || '',
    fundCode: e.fundCode || '',
    fundName: e.fundName || '',
    acctClassCode: e.acctClassCode || '',
    budgetAmt: e.budgetAmt,
    savingAmt: e.savingAmt,
    allocType: e.allocType,
    planConfirmYn:
      metaPatch?.planConfirmYn != null ? metaPatch.planConfirmYn : e.planConfirmYn,
    planConfirmAt:
      metaPatch?.planConfirmAt !== undefined ? metaPatch.planConfirmAt : e.planConfirmAt,
    planConfirmBy:
      metaPatch?.planConfirmBy !== undefined ? metaPatch.planConfirmBy : e.planConfirmBy,
    processYn: e.processYn,
    processAt: e.processAt,
    processBy: e.processBy,
    approveYn: e.approveYn,
    approveAt: e.approveAt,
    approveBy: e.approveBy,
    changeNo: e.changeNo || '',
    changeUid: e.changeUid || '',
    changeDetailUid: e.changeDetailUid || '',
    changeAmt: e.changeAmt || 0,
  };
  MONTH_FIELDS.forEach((f) => {
    out[f] = e[f];
  });
  return out;
}

export function createAllocUid() {
  return `alloc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/* ─── 계획 ─── */

export function getPlanMeta(dims) {
  if (isBdApiEnabled()) {
    const cached = cacheGet(planMetaCache, dimsKey(PLAN_PREFIX, dims));
    if (cached) return cached;
    return { planConfirmYn: 'N', planConfirmAt: null, planConfirmBy: '' };
  }
  const meta = readMeta(dimsKey(PLAN_PREFIX, dims));
  return {
    planConfirmYn: String(meta.planConfirmYn || '').toUpperCase() === 'Y' ? 'Y' : 'N',
    planConfirmAt: meta.planConfirmAt || null,
    planConfirmBy: meta.planConfirmBy || '',
  };
}

export async function loadAllocPlans(dims) {
  const key = dimsKey(PLAN_PREFIX, dims);
  if (isBdApiEnabled()) {
    const rows = (await commonSelect('BdBudgetExec.selectAllocPlanList', allocQueryDims(dims))).map(
      mapAllocRowFromApi
    );
    cacheSet(planCache, key, rows);
    cacheSet(planMetaCache, key, derivePlanMeta(rows));
    return rows;
  }
  const rows = readRows(key).map((r) => enrichAllocRow(r));
  cacheSet(planCache, key, rows);
  return rows;
}

export async function saveAllocPlans(dims, rows, metaPatch = {}) {
  const prev = getPlanMeta(dims);
  const nextMeta = {
    planConfirmYn: metaPatch.planConfirmYn != null ? metaPatch.planConfirmYn : prev.planConfirmYn,
    planConfirmAt: metaPatch.planConfirmAt !== undefined ? metaPatch.planConfirmAt : prev.planConfirmAt,
    planConfirmBy: metaPatch.planConfirmBy !== undefined ? metaPatch.planConfirmBy : prev.planConfirmBy,
  };
  const cleaned = (rows || []).map((r) => persistShape(r, nextMeta));

  if (isBdApiEnabled()) {
    const addValue = allocQueryDims(dims);
    const dbRows = await commonSelect('BdBudgetExec.selectAllocPlanList', addValue);
    const dbUids = new Set(dbRows.map((r) => r.allocUid));
    const payload = cleaned.map((r) => ({
      ...r,
      allocUid: r.allocUid || createAllocUid(),
      mod: dbUids.has(r.allocUid) ? 'U' : 'A',
    }));
    const deleted = dbRows
      .filter((r) => !payload.some((p) => p.allocUid === r.allocUid))
      .map((r) => ({ allocUid: r.allocUid, mod: 'D' }));
    if (deleted.length) await commonSaveList(PLAN_SAVE_QUERY, deleted, addValue);
    if (payload.length) await commonSaveList(PLAN_SAVE_QUERY, payload, addValue);
    const saved = await loadAllocPlans(dims);
    cacheSet(planMetaCache, dimsKey(PLAN_PREFIX, dims), nextMeta);
    return saved;
  }

  writeRows(dimsKey(PLAN_PREFIX, dims), cleaned, nextMeta);
  cacheSet(planCache, dimsKey(PLAN_PREFIX, dims), cleaned.map((r) => enrichAllocRow(r)));
  cacheSet(planMetaCache, dimsKey(PLAN_PREFIX, dims), nextMeta);
  return cleaned.map((r) => enrichAllocRow(r));
}

export async function setPlanConfirm(dims, yn, userId) {
  const rows = await loadAllocPlans(dims);
  const nextYn = yn === 'Y' ? 'Y' : 'N';
  await saveAllocPlans(dims, rows, {
    planConfirmYn: nextYn,
    planConfirmAt: nextYn === 'Y' ? new Date().toISOString() : null,
    planConfirmBy: nextYn === 'Y' ? String(userId || '') : '',
  });
  return getPlanMeta(dims);
}

/**
 * 계획 조회용 행 조립
 * - 현액(확정) 마스터 + 계획 저장본
 * - 계획이 비어 있으면 편성 월별예산으로 시드 (동일 월 배분)
 */
export async function buildPlanRowsForSearch(dims) {
  const masters = mastersFromConfirmedBalances(dims);
  const savedPlan = await loadAllocPlans(dims);
  let seed = savedPlan;
  if (!savedPlan.length) {
    const monthly = await loadMonthlyBudgets(dims);
    seed = monthly;
  }
  const merged = buildMonthlyRowsFromMasters(masters, seed).map((r) =>
    enrichAllocRow({
      ...r,
      allocType: ALLOC_TYPE.REGULAR,
      planConfirmYn: 'N',
    })
  );
  return withMonthlyGridIds(merged);
}

/* ─── 처리 ─── */

export async function loadAllocProcesses(dims) {
  const key = dimsKey(PROCESS_PREFIX, dims);
  if (isBdApiEnabled()) {
    const rows = (await commonSelect('BdBudgetExec.selectAllocProcessList', allocQueryDims(dims))).map(
      mapAllocRowFromApi
    );
    cacheSet(processCache, key, rows);
    return rows;
  }
  const rows = readRows(key).map((r) => enrichAllocRow(r));
  cacheSet(processCache, key, rows);
  return rows;
}

export async function saveAllocProcesses(dims, rows) {
  const cleaned = (rows || []).map((r) => {
    const p = persistShape(r);
    if (!p.allocUid) p.allocUid = createAllocUid();
    return p;
  });

  if (isBdApiEnabled()) {
    const addValue = allocQueryDims(dims);
    const dbRows = await commonSelect('BdBudgetExec.selectAllocProcessList', addValue);
    const dbUids = new Set(dbRows.map((r) => r.allocUid));
    const payload = cleaned.map((r) => ({
      ...r,
      mod: dbUids.has(r.allocUid) ? 'U' : 'A',
    }));
    const keepUids = new Set(payload.map((r) => r.allocUid));
    const deleted = dbRows
      .filter((r) => !keepUids.has(r.allocUid))
      .map((r) => ({ allocUid: r.allocUid, mod: 'D' }));
    if (deleted.length) await commonSaveList(PROCESS_SAVE_QUERY, deleted, addValue);
    if (payload.length) await commonSaveList(PROCESS_SAVE_QUERY, payload, addValue);
    return loadAllocProcesses(dims);
  }

  writeRows(dimsKey(PROCESS_PREFIX, dims), cleaned);
  const enriched = cleaned.map((r) => enrichAllocRow(r));
  cacheSet(processCache, dimsKey(PROCESS_PREFIX, dims), enriched);
  return enriched;
}

/** 정기: 확정된 계획 → 처리 초안 (기존 정기 처리 월 유지) */
export async function buildRegularProcessRows(dims) {
  const meta = getPlanMeta(dims);
  if (meta.planConfirmYn !== 'Y') {
    return { ok: false, reason: '예산배정계획이 확정되지 않았습니다. 계획수립에서 확정 후 처리하세요.', rows: [] };
  }
  const plans = await loadAllocPlans(dims);
  const existing = (await loadAllocProcesses(dims)).filter((r) => r.allocType === ALLOC_TYPE.REGULAR);
  const byReq = new Map();
  const byKey = new Map();
  existing.forEach((r) => {
    const req = String(r.requestUid || '').trim();
    if (req) byReq.set(req, r);
    const lk = String(r.lineKey || monthlyLineKey(r)).trim();
    if (lk) byKey.set(lk, r);
  });

  const rows = plans.map((p) => {
    const req = String(p.requestUid || '').trim();
    const lk = String(p.lineKey || monthlyLineKey(p)).trim();
    const prev = (req && byReq.get(req)) || byKey.get(lk) || {};
    const months = {};
    MONTH_FIELDS.forEach((f) => {
      months[f] = prev[f] != null ? prev[f] : p[f];
    });
    return enrichAllocRow({
      ...emptyMonthAmounts(),
      ...p,
      ...months,
      allocUid: prev.allocUid || createAllocUid(),
      allocType: ALLOC_TYPE.REGULAR,
      budgetAmt: p.budgetAmt,
      savingAmt: prev.savingAmt != null ? prev.savingAmt : p.savingAmt,
      processYn: prev.processYn || 'N',
      processAt: prev.processAt || null,
      processBy: prev.processBy || '',
      approveYn: prev.approveYn || 'N',
      approveAt: prev.approveAt || null,
      approveBy: prev.approveBy || '',
    });
  });
  return { ok: true, rows: withMonthlyGridIds(rows) };
}

/** 수시: 저장본 수시 + (옵션) 계획에 없는 현액 라인 후보 */
export async function buildAdhocProcessRows(dims, { includeCandidates = true } = {}) {
  const existing = (await loadAllocProcesses(dims)).filter((r) => r.allocType === ALLOC_TYPE.ADHOC);
  if (!includeCandidates) {
    return withMonthlyGridIds(existing.map((r) => enrichAllocRow(r)));
  }
  const planKeys = new Set(
    (await loadAllocPlans(dims)).map((r) => String(r.lineKey || monthlyLineKey(r)).trim())
  );
  const masters = mastersFromConfirmedBalances(dims).filter((m) => {
    const lk = monthlyLineKey(m);
    return lk && !planKeys.has(lk);
  });
  const byReq = new Map();
  const byKey = new Map();
  existing.forEach((r) => {
    const req = String(r.requestUid || '').trim();
    if (req) byReq.set(req, r);
    const lk = String(r.lineKey || monthlyLineKey(r)).trim();
    if (lk) byKey.set(lk, r);
  });

  const fromMaster = masters.map((m) => {
    const key = monthlyLineKey(m);
    const req = String(m.requestUid || '').trim();
    const prev = (req && byReq.get(req)) || byKey.get(key) || {};
    return enrichAllocRow({
      ...emptyMonthAmounts(),
      ...prev,
      ...m,
      lineKey: key,
      allocUid: prev.allocUid || createAllocUid(),
      allocType: ALLOC_TYPE.ADHOC,
      budgetAmt: m.budgetAmt,
      savingAmt: prev.savingAmt || 0,
      processYn: prev.processYn || 'N',
      approveYn: prev.approveYn || 'N',
    });
  });

  // 마스터에 없는 기존 수시 행 유지
  const masterKeys = new Set(fromMaster.map((r) => String(r.lineKey || '').trim()));
  const orphans = existing.filter((r) => !masterKeys.has(String(r.lineKey || monthlyLineKey(r)).trim()));
  return withMonthlyGridIds([...fromMaster, ...orphans.map((r) => enrichAllocRow(r))]);
}

export function applyProcessRequest(rows, selectedIds, userId, yn = 'Y') {
  const idSet = new Set((selectedIds || []).map(String));
  const now = new Date().toISOString();
  return (rows || []).map((r) => {
    const id = String(r._rowUid || r.allocUid || r.id || '');
    if (!idSet.has(id) && !idSet.has(String(r.allocUid || ''))) return r;
    if (yn === 'Y' && enrichAllocRow(r).hasUnalloc) {
      return r;
    }
    if (yn === 'Y' && String(r.approveYn || '').toUpperCase() === 'Y') {
      return r;
    }
    return enrichAllocRow({
      ...r,
      processYn: yn === 'Y' ? 'Y' : 'N',
      processAt: yn === 'Y' ? now : null,
      processBy: yn === 'Y' ? String(userId || '') : '',
      mod: r.mod === 'A' ? 'A' : 'U',
    });
  });
}

export function applyApprove(rows, selectedIds, userId, yn = 'Y') {
  const idSet = new Set((selectedIds || []).map(String));
  const now = new Date().toISOString();
  return (rows || []).map((r) => {
    const id = String(r._rowUid || r.allocUid || r.id || '');
    if (!idSet.has(id) && !idSet.has(String(r.allocUid || ''))) return r;
    if (String(r.processYn || '').toUpperCase() !== 'Y') return r;
    return enrichAllocRow({
      ...r,
      approveYn: yn === 'Y' ? 'Y' : 'N',
      approveAt: yn === 'Y' ? now : null,
      approveBy: yn === 'Y' ? String(userId || '') : '',
      mod: r.mod === 'A' ? 'A' : 'U',
    });
  });
}

/**
 * 예산변경 승인(배정처리=Y) → 배정처리 테이블에 수시배정 행 추가
 * - changeNo로 변경 건 추적
 * - 동일 changeUid 기존 연동 행은 교체(재승인·재시도 대비)
 * - PoC: 증감액을 1월(m01)에 넣고 요청·승인 완료 상태로 저장
 */
export async function appendAllocFromBudgetChange(dims, allocLines = [], { userId } = {}) {
  const lines = (allocLines || []).filter((l) => l && (l.changeUid || l.changeNo));
  if (!lines.length) return loadAllocProcesses(dims);

  const changeUid = String(lines[0].changeUid || '').trim();
  const now = new Date().toISOString();
  const existing = (await loadAllocProcesses(dims)).filter(
    (r) => !changeUid || String(r.changeUid || '').trim() !== changeUid
  );

  const created = lines.map((l) => {
    const amt = Number(l.changeAmt) || 0;
    const months = emptyMonthAmounts();
    months.m01 = amt;
    return enrichAllocRow({
      ...months,
      allocUid: createAllocUid(),
      allocType: ALLOC_TYPE.ADHOC,
      deptCode: l.deptCode || '',
      deptName: l.deptName || '',
      projectCode: l.projectCode || '',
      projectName: l.projectName || '',
      subjectCode: l.subjectCode || '',
      subjectName: l.subjectName || '',
      fundCode: l.fundCode || '',
      fundName: l.fundName || '',
      budgetAmt: Math.abs(amt),
      savingAmt: 0,
      changeNo: l.changeNo || '',
      changeUid: l.changeUid || '',
      changeDetailUid: l.changeDetailUid || '',
      changeAmt: amt,
      processYn: 'Y',
      processAt: now,
      processBy: String(userId || ''),
      approveYn: 'Y',
      approveAt: now,
      approveBy: String(userId || ''),
    });
  });

  return saveAllocProcesses(dims, [...existing, ...created]);
}

export { MONTH_FIELDS, distributeMonthly, emptyMonthAmounts, withMonthlyGridIds, enrichMonthlyRow };
